/**
 * Utilitários puros do painel inicial (E13.F4). // ux-neutrality:allow doc-comment
 *
 * Sem chamadas a rede, sem hooks — funções deterministas que serializam o
 * que `HomePage` precisa para renderizar saudação, KPIs, casos recentes
 * e prazos próximos. Mantidas separadas para terem cobertura focada.
 */
import type { CaseRecordData } from './cases';
import { dataCivilEmUtc, diaCivilDeHoje } from './data_civil';

export type GreetingSlot = 'morning' | 'afternoon' | 'evening';

const DAY_MS = 24 * 60 * 60 * 1000;
const NEAR_DAYS = 7;

export function pickGreetingSlot(now: Date): GreetingSlot {
  const hour = now.getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * Extrai um primeiro nome amigável do e-mail. Convenções:
 *   alice@example.com    → "Alice"
 *   ana.maria@x.com      → "Ana"
 *   perito_grafo@y.com   → "Perito"
 *   (string vazia)       → "" (consumer decide fallback)
 */
export function deriveFirstName(email: string | null | undefined): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  const firstChunk = local.split(/[._-]/)[0] ?? '';
  if (firstChunk.length === 0) return '';
  return firstChunk.charAt(0).toUpperCase() + firstChunk.slice(1).toLowerCase();
}

export interface PanelKpis {
  casosNoMes: number;
  ativos: number;
  aguardandoLaudo: number;
}

export function computePanelKpis(cases: readonly CaseRecordData[], now: Date): PanelKpis {
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  return cases.reduce<PanelKpis>(
    (acc, c) => {
      const created = new Date(c.created_at);
      if (created.getUTCFullYear() === year && created.getUTCMonth() === month) {
        acc.casosNoMes += 1;
      }
      // Saudação diz "casos em análise" — contar apenas `analysing`. Rascunhos
      // (status=draft) NÃO contam aqui (F-E13.F4-02). Para "trabalho ativo
      // geral" o painel também mostra a section Continuar trabalhando.
      if (c.status === 'analysing') acc.ativos += 1;
      if (c.status === 'conflict') acc.aguardandoLaudo += 1;
      return acc;
    },
    { casosNoMes: 0, ativos: 0, aguardandoLaudo: 0 },
  );
}

/**
 * E224.F1 (owner, 05/11): o caso assinado, homologado ou encerrado na triagem não pede trabalho, mesmo
 * com o status do caso em `analysing` — o assinado aparecia em "Continuar" e com o prazo atrasado.
 * E224.F2: o cancelado também não.
 */
const TERMINAIS_DA_ESCADA: ReadonlySet<string> = new Set([
  'cancelado', // E224.F2
  'assinado',
  'finalizado',
  'encerrado_triagem',
  'encerrado_com_relatorio',
]);

function semTrabalho(c: CaseRecordData): boolean {
  return c.status === 'done' || TERMINAIS_DA_ESCADA.has(c.workflow_status ?? '');
}

export function pickRecentCases(
  cases: readonly CaseRecordData[],
  limit: number = 3,
): CaseRecordData[] {
  return [...cases]
    .filter((c) => !semTrabalho(c))
    .sort((a, b) => (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at))
    .slice(0, limit);
}

export interface DeadlineEntry {
  case: CaseRecordData;
  daysAway: number;
}

export function pickUpcomingDeadlines(
  cases: readonly CaseRecordData[],
  now: Date,
): DeadlineEntry[] {
  // E176.F1.C01: o "hoje" é o dia civil de quem opera (às 22h em Brasília o dia UTC já é amanhã).
  const today = diaCivilDeHoje(now);
  return cases
    .filter((c) => c.deadline !== null && c.deadline !== undefined && !semTrabalho(c))
    .map((c) => {
      const deadlineDate = dataCivilEmUtc(c.deadline ?? '').getTime();
      const days = Math.floor((deadlineDate - today.getTime()) / DAY_MS);
      return { case: c, daysAway: days };
    })
    .filter((entry) => entry.daysAway <= NEAR_DAYS)
    .sort((a, b) => a.daysAway - b.daysAway);
}

export function targetPathForCase(c: CaseRecordData): string {
  return c.status === 'draft' ? `/casos/${c.id}/wizard` : `/casos/${c.id}/workspace`;
}
